import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { getLocalStorageItem } from '../../utils/helper';

/**
 * Route breadcrumb for the topbar.
 *
 * Derived from the URL rather than declared per page, so a new route gets a
 * trail without anyone remembering to add one. A page with a trail the URL
 * cannot express (a record's name, say) passes `items` instead.
 *
 * Two things the URL alone gets wrong, both handled below:
 *  - the company slug is a tenant identifier, not a place you can navigate to,
 *    so it is dropped rather than rendered as a crumb;
 *  - a Mongo id or other opaque segment reads as noise, so it renders as the
 *    parent's singular ("User") instead of the raw id.
 */

/**
 * Segments whose prettified form would be wrong or unhelpful. Anything not
 * listed falls back to title-casing the segment, which covers most routes.
 */
const SEGMENT_LABELS = {
  dashboard: 'Dashboard',
  'kids-dashboard': 'Kids Dashboard',
  'partner-dashboard': 'Partner Dashboard',
  users: 'Users',
  sleep: 'Sleep',
  sounds: 'Sounds',
  breathwork: 'Breathwork',
  habits: 'Habit Tracker',
  audio: 'Audio',
  badges: 'Badges',
  'pulse-survey': 'Pulse Survey',
  'self-assessment': 'Shoorah Test',
  'content-management': 'Content Management',
  'content-approval': 'Content Approval',
  'people-plan': 'People Plan',
  'salary-sacrifice': 'Salary Sacrifice',
  'instant-support': 'Instant Support',
  'team-challenges': 'Team Challenges',
  'my-company': 'My Company',
  'child-companies': 'Child Companies',
  'change-password': 'Change Password',
  notifications: 'Notifications',
  offerings: 'Offerings',
  marketplace: 'Marketplace',
  reports: 'Reports',
  surveys: 'Surveys',
  integrations: 'Integrations',
  solution: 'Solutions',
  add: 'Add',
  edit: 'Edit',
  view: 'View',
  // camelCase routes that would otherwise prettify to a restatement of their
  // parent — "Pod Experts > Edit Pod Experts" says the same thing twice.
  podExperts: 'Experts',
  addPodExperts: 'Add',
  editPodExperts: 'Edit'
};

/** Mongo ids and numeric ids — real segments, but meaningless as labels. */
const isOpaqueSegment = (segment) =>
  /^[0-9a-f]{24}$/i.test(segment) || /^\d+$/.test(segment);

const prettify = (segment) =>
  segment
    .replace(/[-_]/g, ' ')
    // camelCase is a route spelling, not a label: `viewCompany` is two words.
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const singular = (label = '') => label.replace(/ies$/, 'y').replace(/s$/, '');

/**
 * After "Add", "Edit" or "View" the record's id adds nothing — the crumb would
 * read "Edit > Expert" where "Edit" already said where you are.
 */
const ACTION_LABELS = ['Add', 'Edit', 'View'];

const Breadcrumbs = ({ items, className = '' }) => {
  const location = useLocation();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const crumbs = useMemo(() => {
    if (items?.length) return items;

    const segments = location.pathname.split('/').filter(Boolean);

    // The tenant slug prefixes every company-panel route and is not a place.
    const path = slug && segments[0] === slug ? segments.slice(1) : segments;

    const home = slug ? `/${slug}/dashboard` : '/dashboard';
    const trail = [{ label: 'Home', path: home }];

    path.forEach((segment, index) => {
      const previous = trail[trail.length - 1]?.label;
      if (isOpaqueSegment(segment) && ACTION_LABELS.includes(previous)) return;

      const href = `${slug ? `/${slug}` : ''}/${path.slice(0, index + 1).join('/')}`;
      const label = isOpaqueSegment(segment)
        ? singular(previous || 'Detail')
        : SEGMENT_LABELS[segment] || prettify(segment);
      trail.push({ label, path: href });
    });

    return trail;
  }, [items, location.pathname, slug]);

  // A single crumb is just "Home" — the title above already says as much.
  if (crumbs.length < 2) return null;

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-xs text-shoorah-gray3">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li key={`${crumb.path}-${index}`} className="flex items-center gap-1">
              {index > 0 && <ChevronRightIcon className="w-3.5 h-3.5 shrink-0 opacity-60" />}
              {isLast || !crumb.path ? (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className="font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg"
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  state={crumb.state}
                  className="hover:text-shoorah-lightBlack dark:hover:text-shoorah-darkBg hover:underline"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumbs.propTypes = {
  /** Explicit trail, for pages whose hierarchy the URL cannot express. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      // Router state for pages that load from it, such as a user profile
      state: PropTypes.any
    })
  ),
  className: PropTypes.string
};

export default Breadcrumbs;
